/**
 * POP T31: authored cast looks. A named character's `appearance` is `cast:<key>`, a key of `art/pop/catalog/cast.json`.
 * `castList.g.json` beside this file is written by `art/pop/catalog/gen_catalog.py` (never edit it); it carries each entry's
 * name, sex, notes and where it appears, for the content forms' cast dropdown. Mirrors shared-cs `ContentValidator.Cast.cs`.
 */
import castList from './castList.g.json';
import type { Err } from './common';
import type { Bundle } from './types';

export type CastLook = {
  key: string;
  name: string;
  sex: 'm' | 'f';
  code: string;
  child?: boolean;
  preset?: boolean;
  uniform?: boolean;
  group: string;
  appears: string[];
  notes: string;
};

export const CAST_PREFIX = 'cast:';

/** Every authored cast look, in cast.json order. */
export const CAST_LOOKS: CastLook[] = (castList as { cast: CastLook[] }).cast;

const KEYS = new Set(CAST_LOOKS.map((c) => c.key));

/** The cast key of a reference, or null when it isn't one. */
export const castKeyOf = (ref: string | null | undefined): string | null =>
  ref && ref.startsWith(CAST_PREFIX) && ref.length > CAST_PREFIX.length
    ? ref.slice(CAST_PREFIX.length)
    : null;

/** The C# `CastLooks.Problem`: '' when empty or valid. */
export const castProblem = (
  ref: string | null | undefined,
): '' | 'not_a_cast_reference' | 'unknown_cast_key' => {
  if (!ref) return '';
  const key = castKeyOf(ref);
  if (key == null) return 'not_a_cast_reference';
  return KEYS.has(key) ? '' : 'unknown_cast_key';
};

type WithAppearance = { appearance?: string | null } | null | undefined;
const appearanceOf = (x: unknown) => (x as WithAppearance)?.appearance;

function check(
  err: Err,
  section: Parameters<Err>[0],
  index: number | undefined,
  path: string,
  where: string,
  ref: string | null | undefined,
) {
  const p = castProblem(ref);
  if (p === 'not_a_cast_reference')
    err(section, index, path, `${where} appearance '${ref}' must be cast:<key>.`);
  else if (p === 'unknown_cast_key')
    err(section, index, path, `${where} appearance '${ref}' names no cast look.`);
}

/** `appearance` on npcs[], allies[], arcs[].cast[] and crew roster entries. */
export function validateCastLooks(b: Bundle, err: Err) {
  (b.npcs ?? []).forEach(
    (n, i) => n && check(err, 'npcs', i, 'appearance', `NPC '${n.id ?? ''}'`, appearanceOf(n)),
  );
  (b.allies ?? []).forEach(
    (a, i) => a && check(err, 'allies', i, 'appearance', `Ally '${a.id ?? ''}'`, appearanceOf(a)),
  );
  const arcs = (b as { arcs?: ({ id?: string; cast?: ({ id?: string } | null)[] } | null)[] }).arcs;
  (arcs ?? []).forEach((arc, i) =>
    (arc?.cast ?? []).forEach(
      (c, j) =>
        c &&
        check(
          err,
          'arcs' as Parameters<Err>[0],
          i,
          `cast.${j}.appearance`,
          `Arc '${arc!.id ?? ''}' cast '${c.id ?? ''}'`,
          appearanceOf(c),
        ),
    ),
  );
  const crews = (
    b as {
      crews?: ({ id?: string; roster?: ({ name?: string; archetype?: string } | null)[] } | null)[];
    }
  ).crews;
  (crews ?? []).forEach((crew, i) =>
    (crew?.roster ?? []).forEach(
      (m, j) =>
        m &&
        check(
          err,
          'crews' as Parameters<Err>[0],
          i,
          `roster.${j}.appearance`,
          `Crew '${crew!.id ?? ''}' roster member '${m.name ? m.name : (m.archetype ?? '')}'`,
          appearanceOf(m),
        ),
    ),
  );
}
